"use client";

import { useEffect, useRef, type CSSProperties } from "react";

/** An icon's resting pose; its frames live in public/images/icons/<name>.json. */
export interface AnimatedIconData {
  name: string;
  viewBox: string;
  d: string;
}

interface Frames {
  /** Milliseconds per frame. */
  ms: number;
  /** Playback order, as indexes into `f`. */
  s: number[];
  /** Path data of each frame. */
  f: string[];
}

const frameCache = new Map<string, Promise<Frames>>();

/** Fetches an icon's frames once per page; a failed fetch can be retried. */
async function fetchFrames(name: string): Promise<Frames> {
  const res = await fetch(`/images/icons/${name}.json`);
  if (!res?.ok) throw new Error(`Animated icon "${name}" failed to load`);
  return res.json();
}

function loadFrames(name: string): Promise<Frames> {
  let frames = frameCache.get(name);
  if (!frames) {
    frames = fetchFrames(name);
    frames.catch(() => frameCache.delete(name));
    frameCache.set(name, frames);
  }
  return frames;
}

const reducedMotion = () =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

export interface AnimatedIconProps {
  icon: AnimatedIconData;
  className?: string;
  style?: CSSProperties;
  /** Play once on entering the viewport, after this many ms. */
  playOnView?: number;
  /** Ancestor whose hover, press or keyboard focus replays the icon. */
  host?: string;
}

/**
 * Frame-animated line icon from heyfinn.ai: renders its final pose, and plays
 * the vector frames (75 × 20 ms) on view and on hover of its link or button.
 * Takes `currentColor`; never plays under prefers-reduced-motion.
 */
export function AnimatedIcon({
  icon,
  className = "",
  style,
  playOnView,
  host = "a,button",
}: AnimatedIconProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const pathRef = useRef<SVGPathElement>(null);

  useEffect(() => {
    const svg = svgRef.current;
    const path = pathRef.current;
    if (!svg || !path) return;
    let raf = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let alive = true;

    const play = async (delay = 0) => {
      if (reducedMotion()) return;
      let frames: Frames;
      try {
        frames = await loadFrames(icon.name);
      } catch {
        return;
      }
      if (!alive) return;
      cancelAnimationFrame(raf);
      clearTimeout(timer);
      timer = setTimeout(() => {
        let start = 0;
        let shown = -1;
        const step = (now: number) => {
          start ||= now;
          const i = Math.floor((now - start) / frames.ms);
          if (i >= frames.s.length) {
            path.setAttribute("d", icon.d);
            return;
          }
          if (frames.s[i] !== shown) {
            shown = frames.s[i];
            path.setAttribute("d", frames.f[shown]);
          }
          raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
      }, delay);
    };

    const hostEl = host ? svg.parentElement?.closest(host) : null;
    const onEnter = (e: PointerEvent) => {
      if (e.pointerType === "mouse") play();
    };
    const onDown = () => play();
    const onFocus = (e: FocusEvent) => {
      if ((e.target as Element).matches?.(":focus-visible")) play();
    };
    hostEl?.addEventListener("pointerenter", onEnter as EventListener);
    hostEl?.addEventListener("pointerdown", onDown);
    hostEl?.addEventListener("focusin", onFocus as EventListener);

    let observer: IntersectionObserver | undefined;
    if (
      playOnView !== undefined &&
      typeof IntersectionObserver !== "undefined"
    ) {
      observer = new IntersectionObserver(
        entries => {
          if (entries.some(entry => entry.isIntersecting)) {
            observer?.disconnect();
            play(playOnView);
          }
        },
        { threshold: 0.5 }
      );
      observer.observe(svg);
    }

    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      clearTimeout(timer);
      observer?.disconnect();
      hostEl?.removeEventListener("pointerenter", onEnter as EventListener);
      hostEl?.removeEventListener("pointerdown", onDown);
      hostEl?.removeEventListener("focusin", onFocus as EventListener);
      path.setAttribute("d", icon.d);
    };
  }, [icon, playOnView, host]);

  return (
    <svg
      ref={svgRef}
      viewBox={icon.viewBox}
      fill="currentColor"
      fillRule="evenodd"
      aria-hidden="true"
      focusable="false"
      className={`flex-none overflow-visible ${className}`}
      style={style}
    >
      <path ref={pathRef} d={icon.d} />
    </svg>
  );
}
